import { Menu, Wallet, PanelLeftOpen, PanelLeftClose } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { dashboardAPI } from '../../api/dashboard'
import { formatCurrency } from '../../utils/helpers'
import Skeleton from '../ui/Skeleton'

const Header = ({ onMenuClick, onDesktopSidebarToggle, isDesktopSidebarOpen = true }) => {
  const { isAuthenticated } = useAuth()

  // Fetch merchant name and balances
  const { data: merchantData, isLoading } = useQuery({
    queryKey: ['merchant-name'],
    queryFn: dashboardAPI.getMerchantName,
    enabled: isAuthenticated,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  })

  if (!isAuthenticated) return null

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 lg:px-6 gap-4">
        <div className="flex items-center gap-2">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <Menu size={24} />
          </button>
          
          {/* Desktop Sidebar Toggle Button */}
          <button
            onClick={onDesktopSidebarToggle}
            className="hidden lg:flex p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            title={isDesktopSidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
          >
            {isDesktopSidebarOpen ? (
              <PanelLeftClose size={24} />
            ) : (
              <PanelLeftOpen size={24} />
            )}
          </button>
        </div>
        
        {/* Merchant Name - Centered */}
        <div className="flex-1 flex justify-center min-w-0">
          {isLoading ? (
            <Skeleton variant="text" className="w-48 h-6" />
          ) : (
            <h2 className="text-lg font-bold text-gray-900 text-center">
              👋 Hello {merchantData?.merchantName || 'Merchant'} {merchantData?.merchantId && `(${merchantData.merchantId})`}
            </h2>
          )}
        </div>

        {/* Wallet Balances - Always Visible */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {isLoading ? (
            <>
              <Skeleton variant="text" className="w-28 sm:w-36 h-14" />
              <Skeleton variant="text" className="w-28 sm:w-36 h-14" />
            </>
          ) : (
            <>
              {/* PayIn Wallet Card */}
              <div className="relative px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl transition-all hover:shadow-lg hover:scale-105 min-w-[110px] sm:min-w-[130px] overflow-hidden group">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
                    <Wallet className="text-white" size={18} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] sm:text-[10px] text-green-600 font-semibold uppercase tracking-wide mb-0.5">
                      PayIn
                    </div>
                    <div className="text-xs sm:text-sm font-bold text-green-700 leading-tight truncate">
                      {formatCurrency(parseFloat(merchantData?.payInWalletBalance || 0))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payout Wallet Card */}
              <div className="relative px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl transition-all hover:shadow-lg hover:scale-105 min-w-[110px] sm:min-w-[130px] overflow-hidden group">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
                    <Wallet className="text-white" size={18} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] sm:text-[10px] text-blue-600 font-semibold uppercase tracking-wide mb-0.5">
                      Payout
                    </div>
                    <div className="text-xs sm:text-sm font-bold text-blue-700 leading-tight truncate">
                      {formatCurrency(parseFloat(merchantData?.payOutWalletBalance || 0))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header

